import moment from "moment";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import {
  texto,
  numero,
  moneda,
  fecha,
  totalizar,
  construirHojaTabla,
  limpiarNombreArchivo,
  paraPdf,
  descargarLibro,
} from "../../../../Components/ExcelTablaReporte";
import reporteFiscalPdf from "../../../../Pdf/reporteFiscalPdf";

pdfMake.vfs = pdfFonts.pdfMake.vfs;

/**
 * Excel y PDF del Reporte de Facturas Globales.
 *
 * Dos tablas sobre el mismo periodo: las facturas globales emitidas y las
 * remisiones que siguen pendientes de globalizar. La segunda es la de auditoria.
 */

const COLUMNAS_EMITIDAS = [
  { titulo: "Folio", tipo: "texto", ancho: 16, anchoPdf: 60 },
  { titulo: "UUID", tipo: "texto", ancho: 38, enPdf: false },
  { titulo: "Fecha", tipo: "fecha", ancho: 12, anchoPdf: 52 },
  { titulo: "Periodicidad", tipo: "texto", ancho: 16, anchoPdf: 62 },
  { titulo: "Mes", tipo: "texto", ancho: 14, anchoPdf: 58 },
  { titulo: "Ano", tipo: "numero", ancho: 8, anchoPdf: 34 },
  { titulo: "Proyecto", tipo: "texto", ancho: 26, anchoPdf: 100 },
  { titulo: "Moneda", tipo: "texto", ancho: 9, enPdf: false },
  { titulo: "Remisiones", tipo: "numero", ancho: 11, anchoPdf: 44 },
  { titulo: "Subtotal", tipo: "moneda", ancho: 16 },
  { titulo: "Impuestos", tipo: "moneda", ancho: 16 },
  { titulo: "Total", tipo: "moneda", ancho: 16 },
  { titulo: "Detalle", tipo: "texto", ancho: 14, enPdf: false },
  { titulo: "Estado", tipo: "texto", ancho: 12, anchoPdf: 52 },
];

const COLUMNAS_PENDIENTES = [
  { titulo: "Folio", tipo: "texto", ancho: 16, anchoPdf: 60 },
  { titulo: "Fecha", tipo: "fecha", ancho: 12, anchoPdf: 54 },
  { titulo: "Cliente", tipo: "texto", ancho: 34, anchoPdf: 150 },
  { titulo: "RFC", tipo: "texto", ancho: 16, anchoPdf: 76 },
  { titulo: "Proyecto", tipo: "texto", ancho: 26, anchoPdf: 106 },
  { titulo: "Forma de pago", tipo: "texto", ancho: 24, enPdf: false },
  { titulo: "Subtotal", tipo: "moneda", ancho: 16 },
  { titulo: "Impuestos", tipo: "moneda", ancho: 16 },
  { titulo: "Total", tipo: "moneda", ancho: 16 },
  { titulo: "Cobrado", tipo: "moneda", ancho: 16, enPdf: false },
];

const COLUMNAS_DETALLE = [
  { titulo: "Folio factura global", tipo: "texto", ancho: 20 },
  { titulo: "Folio remision", tipo: "texto", ancho: 16 },
  { titulo: "Fecha", tipo: "fecha", ancho: 12 },
  { titulo: "Cliente", tipo: "texto", ancho: 34 },
  { titulo: "RFC", tipo: "texto", ancho: 16 },
  { titulo: "Subtotal", tipo: "moneda", ancho: 16 },
  { titulo: "Impuestos", tipo: "moneda", ancho: 16 },
  { titulo: "Total", tipo: "moneda", ancho: 16 },
  { titulo: "Estado", tipo: "texto", ancho: 16 },
];

export const ESTADO_GLOBAL = { 0: "Cancelada", 1: "Vigente" };

const anioDe = (registro) => {
  const fechaPeriodo = moment(registro.anoPeriocidad);
  return fechaPeriodo.isValid() ? fechaPeriodo.year() : null;
};

export const construirTablaEmitidas = (registros) => {
  const filas = (registros || []).map((registro) => [
    texto(registro.serieNumero
      || [registro.facturaSerie, registro.facturaNumero].filter(Boolean).join(" - ")),
    texto(registro.uuid),
    fecha(registro.fechaFactura),
    texto(registro.periocidad),
    texto(registro.mes),
    numero(anioDe(registro)),
    texto(registro.proyectoNombre),
    texto(registro.moneda),
    numero(registro.ventas),
    moneda(registro.subtotal),
    moneda(registro.impuestos),
    moneda(registro.total),
    // `porVenta` cambia el CFDI: una linea por remision en vez de una por insumo.
    texto(registro.porVenta ? "Por venta" : "Por insumo"),
    texto(ESTADO_GLOBAL[registro.estado] !== undefined ? ESTADO_GLOBAL[registro.estado] : ""),
  ]);

  return { columnas: COLUMNAS_EMITIDAS, filas, totales: totalizar(COLUMNAS_EMITIDAS, filas) };
};

export const construirTablaPendientes = (registros) => {
  const filas = (registros || []).map((registro) => [
    texto(registro.serieNumero
      || [registro.remisionSerie, registro.remisionNumero].filter(Boolean).join(" - ")),
    fecha(registro.fechaRemision),
    texto(registro.nombreCliente),
    texto(registro.rfcCliente),
    texto(registro.proyectoNombre),
    texto(registro.formaDePago),
    moneda(registro.subtotal),
    moneda(registro.impuestos),
    moneda(registro.total),
    moneda(registro.totalPagado),
  ]);

  return { columnas: COLUMNAS_PENDIENTES, filas, totales: totalizar(COLUMNAS_PENDIENTES, filas) };
};

/**
 * El detalle sólo entra al Excel cuando ya se cargó (se pide al expandir el
 * renglón, no con el listado). `detalles` es `{ facturaGlobalId: { folio, ventas } }`.
 */
export const construirTablaDetalle = (registros, detalles) => {
  const filas = [];
  for (const registro of registros || []) {
    const detalle = detalles ? detalles[registro._id] : null;
    if (!detalle || !detalle.ventas) continue;
    const folioGlobal = registro.serieNumero
      || [registro.facturaSerie, registro.facturaNumero].filter(Boolean).join(" - ");
    for (const venta of detalle.ventas) {
      filas.push([
        texto(folioGlobal),
        texto(venta.folio),
        fecha(venta.fechaRemision),
        texto(venta.nombreCliente),
        texto(venta.rfcCliente),
        moneda(venta.subtotal),
        moneda(venta.impuestos),
        moneda(venta.total),
        texto(venta.facturaDeGlobal ? "Extraida de la global" : (venta.estado === 0 ? "Cancelada" : "Vigente")),
      ]);
    }
  }

  return { columnas: COLUMNAS_DETALLE, filas, totales: totalizar(COLUMNAS_DETALLE, filas) };
};

const TITULO_EMITIDAS = "Facturas Globales emitidas";
const TITULO_PENDIENTES = "Remisiones pendientes de globalizar";

const nombreArchivo = (empresaNombre) =>
  `Facturas_Globales_${limpiarNombreArchivo(empresaNombre)}_${moment().format("YYYY-MM-DD")}`;

/**
 * @param {Object} params
 * @param {Object[]} params.emitidas    facturas globales del periodo
 * @param {Object[]} params.pendientes  remisiones sin globalizar
 * @param {Object} [params.detalles]    detalle ya cargado, por facturaGlobalId
 */
export const descargarExcel = ({
  emitidas, pendientes, detalles, empresaNombre, descripcionFiltros,
}) => {
  const encabezado = { empresaNombre, descripcionFiltros };
  const hojas = [
    {
      nombre: "Facturas Globales",
      hoja: construirHojaTabla(construirTablaEmitidas(emitidas), {
        ...encabezado,
        titulo: TITULO_EMITIDAS,
      }),
    },
    {
      nombre: "Remisiones Pendientes",
      hoja: construirHojaTabla(construirTablaPendientes(pendientes), {
        ...encabezado,
        titulo: TITULO_PENDIENTES,
      }),
    },
  ];

  const tablaDetalle = construirTablaDetalle(emitidas, detalles);
  if (tablaDetalle.filas.length > 0) {
    hojas.push({
      nombre: "Detalle Remisiones",
      hoja: construirHojaTabla(tablaDetalle, {
        ...encabezado,
        titulo: "Remisiones incluidas en cada factura global",
      }),
    });
  }

  descargarLibro(hojas, nombreArchivo(empresaNombre));
};

export const descargarPdf = ({
  emitidas, pendientes, empresaNombre, descripcionFiltros, totalesEmitidas = {}, totalesPendientes = {},
}) => {
  const tablaEmitidas = construirTablaEmitidas(emitidas);
  const tablaPendientes = construirTablaPendientes(pendientes);

  pdfMake
    .createPdf(
      reporteFiscalPdf({
        titulo: "Reporte de Facturas Globales",
        empresaNombre,
        descripcionFiltros,
        indicadores: [
          { etiqueta: "Globales emitidas", valor: String(totalesEmitidas.documentos || 0) },
          { etiqueta: "Canceladas", valor: String(totalesEmitidas.canceladas || 0) },
          { etiqueta: "Remisiones globalizadas", valor: String(totalesEmitidas.ventas || 0) },
          { etiqueta: "Pendientes de globalizar", valor: String(totalesPendientes.documentos || 0) },
        ],
        contador: tablaEmitidas.filas.length,
        secciones: [
          { titulo: TITULO_EMITIDAS, ...paraPdf(tablaEmitidas) },
          { titulo: TITULO_PENDIENTES, ...paraPdf(tablaPendientes) },
        ],
      })
    )
    .download(`${nombreArchivo(empresaNombre)}.pdf`);
};
